import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  agregarDigito, borrarDigito, esBloqueo, esperaSegura, mensajeRespuesta, normalizarRespuesta, textoDigitos,
} from './tecladoPinLogica';

// Teclado de PIN compartido de VLS (PIN central, 2026-10-21). Reúne lo mejor de los cinco teclados que había:
//   - cables (src/components/TecladoPin.jsx): presentacional, teclas de 64 px o más, aria por tecla, el PIN solo
//     en el estado (sin <input>, sin storage, sin URL), teclado físico acotado a la pantalla, bloqueo por HORA
//     LÍMITE (un teléfono dormido no atrasa el desbloqueo) y "sin espera válida = 60 s";
//   - mantenimiento y marcas (src/app/TecladoPin.jsx): puntos grandes, envío solo al completar los 4 números,
//     vibración y sacudida al fallar, "Le quedan N intentos";
//   - equipos (kiosco/piezas.jsx#TecladoNumerico): tecla extra a la izquierda del 0 (ej. "hasta");
//   - seguridad (src/puesto/TecladoPin.jsx): Volver junto al teclado, se usa de noche y con guantes.
//
// Dos piezas:
//   <TecladoPin onEnviar={...} />       con estado: puntos, mensajes, bloqueo, teclado físico.
//   <TecladoNumerico onDigito onBorrar /> la variante ESTÁTICA: solo las 12 teclas, para pantallas que ya llevan
//                                        su propio estado (o para dictar números que no son un PIN).
//
// ─── Migración (mecánica) ────────────────────────────────────────────────
//   ANTES (mantenimiento):  <TecladoPin onEnviar={async (pin) => entrar(pin)} />   // 'ok' | 'invalido' | ...
//   DESPUÉS:                <TecladoPin largo={4} tamano="grande" onEnviar={async (pin) => entrar(pin)} />
//   ANTES (cables):         PinPantalla con su estado + <TecladoPin onDigito onBorrar onEnviar puedeEnviar />
//   DESPUÉS:                <TecladoPin largo={null} min={4} max={8} tamano="normal"
//                             onEnviar={async (pin) => entrarConPin(pin)} />          // { ok, error, ... }
//   Con la central: onEnviar devuelve tal cual la respuesta de /api/pin (acc_pin_verificar).
//
// Props de TecladoPin:
//   onEnviar(pin) → Promise<respuesta>  obligatoria. Respuestas aceptadas: ver normalizarRespuesta (tecladoPinLogica.js).
//                                        Con resultado 'ok' el teclado se limpia y la pantalla que lo usa navega.
//   largo        número (por defecto 4): se envía solo al completar. null: entre min y max, con la tecla Entrar.
//   min, max     solo con largo=null (por defecto 4 y 8).
//   titulo       por defecto "Escriba su PIN".   subtitulo: el nombre de la persona o de la app.
//   tamano       'grande' (kiosco o tablet fija, teclas de 88 px) | 'normal' (teléfono, 68 px).
//   aviso        texto informativo que no es error (ej. "Este teléfono no está en la red del parque").
//   deshabilitado, tecladoFisico (true), onVolver + textoVolver ("Volver"), extra (tecla a la izquierda del 0
//   cuando largo es fijo), children (acciones bajo el teclado, ej. "Marcar sin PIN" de marcas).
//   soloIconos   (mantenimiento, para personas que no leen): sin texto a la vista. 🔢 grande arriba de los puntos;
//                título y mensajes siguen en el DOM solo para el lector de pantalla; con bloqueo, ⏳ con los
//                segundos que faltan en lugar del teclado. Ej.: <TecladoPin largo={4} tamano="grande" soloIconos ... />
// Sin dependencias; colores por var(--color-*) con respaldo, medidas en px (la escala de Tailwind cambia entre apps).

const C = {
  fg: 'var(--color-foreground, #1C1917)',
  mutedFg: 'var(--color-muted-foreground, #78716C)',
  card: 'var(--color-card, #FFFFFF)',
  border: 'var(--color-border, #E7E5E4)',
  muted: 'var(--color-muted, #F5F5F4)',
  primary: 'var(--color-primary, #0F7A3D)',
  primaryFg: 'var(--color-primary-foreground, #FFFFFF)',
  error: 'var(--color-error, #DC2626)',
  warning: 'var(--color-warning, #D97706)',
};

const MEDIDAS = {
  grande: { tecla: 88, letra: 40, gap: 14, ancho: 400, punto: 26, titulo: 32 },
  normal: { tecla: 68, letra: 30, gap: 12, ancho: 340, punto: 18, titulo: 24 },
};

const estiloTactil = { touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', WebkitUserSelect: 'none', userSelect: 'none' };

// Solo para el lector de pantalla (variante soloIconos): en el DOM, fuera de la vista.
const soloLector = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap', border: 0,
};

function IconoBorrar({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
      <line x1="18" y1="9" x2="12" y2="15" />
      <line x1="12" y1="9" x2="18" y2="15" />
    </svg>
  );
}

function Tecla({ etiqueta, onClick, deshabilitado, principal = false, m, children, ...resto }) {
  const [abajo, setAbajo] = useState(false);
  return (
    <button
      type="button"
      aria-label={etiqueta}
      disabled={deshabilitado}
      onClick={onClick}
      onPointerDown={() => setAbajo(true)}
      onPointerUp={() => setAbajo(false)}
      onPointerLeave={() => setAbajo(false)}
      style={{
        ...estiloTactil,
        minHeight: m.tecla,
        minWidth: 64,
        borderRadius: 16,
        border: `2px solid ${principal ? C.primary : C.border}`,
        background: principal ? C.primary : abajo ? C.muted : C.card,
        color: principal ? C.primaryFg : C.fg,
        fontSize: principal ? Math.round(m.letra * 0.55) : m.letra,
        fontWeight: 700,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: deshabilitado ? 'not-allowed' : 'pointer',
        opacity: deshabilitado ? 0.4 : 1,
        transform: abajo && !deshabilitado ? 'scale(0.95)' : 'none',
        transition: 'transform 75ms',
        boxShadow: principal ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
        fontVariantNumeric: 'tabular-nums',
      }}
      {...resto}
    >
      {children}
    </button>
  );
}

// Variante estática: las 12 teclas. Con onEnviar: [Borrar] [0] [Entrar] (largo variable, como cables).
// Sin onEnviar: [extra o vacío] [0] [Borrar] (largo fijo, como mantenimiento, marcas y equipos).
export function TecladoNumerico({
  onDigito, onBorrar, onEnviar, puedeEnviar = false, deshabilitado = false, tamano = 'normal', extra = null,
  textoEnviar = 'Entrar',
}) {
  const m = MEDIDAS[tamano] || MEDIDAS.normal;
  const digito = (d) => (
    <Tecla key={d} etiqueta={d} m={m} deshabilitado={deshabilitado} onClick={() => onDigito?.(d)}>{d}</Tecla>
  );
  const borrar = (
    <Tecla key="borrar" etiqueta="Borrar" m={m} deshabilitado={deshabilitado} onClick={() => onBorrar?.()}>
      <IconoBorrar size={Math.round(m.letra * 0.8)} />
    </Tecla>
  );
  return (
    <div
      data-testid="teclado-numerico"
      style={{ ...estiloTactil, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: m.gap, width: '100%', maxWidth: m.ancho, margin: '0 auto' }}
    >
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(digito)}
      {onEnviar ? borrar : (extra ?? <span aria-hidden="true" />)}
      {digito('0')}
      {onEnviar ? (
        <Tecla key="entrar" etiqueta={textoEnviar} m={m} principal data-tecla="entrar"
          deshabilitado={deshabilitado || !puedeEnviar} onClick={() => onEnviar()}>
          {textoEnviar}
        </Tecla>
      ) : borrar}
    </div>
  );
}

export function PuntosPin({ llenos, total, error = false, tamano = 'normal', refPuntos }) {
  const m = MEDIDAS[tamano] || MEDIDAS.normal;
  return (
    <div ref={refPuntos} role="status" aria-label={`${llenos} de ${total} números`} data-testid="puntos-pin"
      style={{ display: 'flex', justifyContent: 'center', gap: Math.round(m.punto * 0.7) }}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} data-lleno={i < llenos ? 'si' : 'no'} style={{
          width: m.punto, height: m.punto, borderRadius: '50%',
          border: `3px solid ${error ? C.error : i < llenos ? C.primary : C.mutedFg}`,
          background: error ? C.error : i < llenos ? C.primary : C.card,
        }} />
      ))}
    </div>
  );
}

function vibrar() {
  try { navigator.vibrate?.([120, 60, 120]); } catch { /* sin vibración */ }
}

export default function TecladoPin({
  onEnviar, largo = 4, min = 4, max = 8, titulo = 'Escriba su PIN', subtitulo, tamano = 'normal', aviso,
  deshabilitado = false, tecladoFisico = true, onVolver, textoVolver = 'Volver', extra = null, soloIconos = false, children,
  'data-testid': testId = 'teclado-pin',
}) {
  const fijo = Number.isInteger(largo) && largo > 0;
  const minimo = fijo ? largo : min;
  const maximo = fijo ? largo : max;
  const m = MEDIDAS[tamano] || MEDIDAS.normal;
  const idTitulo = `pin-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  // El PIN vive solo aquí (estado + ref para no leer un valor viejo entre dos toques seguidos).
  const [pin, setPin] = useState('');
  const pinRef = useRef('');
  const ponerPin = (v) => { pinRef.current = v; setPin(v); };
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [errorVisual, setErrorVisual] = useState(false);
  // Bloqueo por intentos: se guarda la HORA LÍMITE, no un contador.
  const [hasta, setHasta] = useState(0);
  const [ahora, setAhora] = useState(() => Date.now());
  const enviandoRef = useRef(false);
  const montadoRef = useRef(true);
  const raizRef = useRef(null);
  const puntosRef = useRef(null);

  useEffect(() => {
    montadoRef.current = true;
    return () => { montadoRef.current = false; };
  }, []);

  const restante = hasta ? Math.max(0, Math.ceil((hasta - ahora) / 1000)) : 0;
  const bloqueado = restante > 0;
  const ocupado = deshabilitado || enviando || bloqueado;

  useEffect(() => {
    if (!hasta) return undefined;
    const tic = () => setAhora(Date.now());
    tic();
    const id = setInterval(tic, 1000);
    return () => clearInterval(id);
  }, [hasta]);

  useEffect(() => {
    if (hasta && restante <= 0) {
      setHasta(0);
      setMensaje('');
    }
  }, [hasta, restante]);

  const sacudir = () => {
    vibrar();
    setErrorVisual(true);
    try {
      puntosRef.current?.animate?.(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(-10px)' }, { transform: 'translateX(10px)' }, { transform: 'translateX(0)' }],
        { duration: 400 },
      );
    } catch { /* sin animación */ }
    setTimeout(() => { if (montadoRef.current) setErrorVisual(false); }, 700);
  };

  const enviar = useCallback(async (valor) => {
    if (enviandoRef.current || ocupado) return;
    if (valor.length < minimo || valor.length > maximo) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setMensaje(mensajeRespuesta({ resultado: 'sin_conexion', mensaje: '' }));
      return;
    }
    enviandoRef.current = true;
    setEnviando(true);
    setMensaje('');
    let n;
    try {
      n = normalizarRespuesta(await onEnviar(valor));
    } catch {
      n = normalizarRespuesta(null);
    }
    enviandoRef.current = false;
    if (!montadoRef.current) return;
    setEnviando(false);
    ponerPin('');
    if (n.resultado === 'ok') {
      setMensaje('');
      return;
    }
    sacudir();
    if (esBloqueo(n)) {
      const seg = esperaSegura(n.esperaSeg);
      const t = Date.now();
      setAhora(t);
      setHasta(t + seg * 1000);
      setMensaje(n.mensaje);
      return;
    }
    setMensaje(mensajeRespuesta(n, { min: minimo, max: maximo }));
  }, [ocupado, minimo, maximo, onEnviar]);

  const digito = useCallback((d) => {
    if (enviandoRef.current || ocupado) return;
    const nuevo = agregarDigito(pinRef.current, d, maximo);
    if (nuevo === pinRef.current) return;
    setMensaje('');
    ponerPin(nuevo);
    if (fijo && nuevo.length === largo) enviar(nuevo);
  }, [ocupado, maximo, fijo, largo, enviar]);

  const borrar = useCallback(() => {
    if (enviandoRef.current || ocupado) return;
    ponerPin(borrarDigito(pinRef.current));
  }, [ocupado]);

  // Teclado físico: dígitos, Backspace y Enter, solo con el foco en el teclado o en ningún lado.
  useEffect(() => {
    if (!tecladoFisico) return undefined;
    const alTeclear = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const raiz = raizRef.current;
      const esNodo = !!t && typeof Node !== 'undefined' && t instanceof Node;
      const enCuerpo = !esNodo || t === document || t === document.body || t === document.documentElement;
      if (!enCuerpo && !(raiz && raiz.contains(t))) return;
      if (/^[0-9]$/.test(e.key)) { e.preventDefault(); digito(e.key); }
      else if (e.key === 'Backspace') { e.preventDefault(); borrar(); }
      else if (e.key === 'Enter' && !fijo) {
        if (t && t.tagName === 'BUTTON' && t.getAttribute('data-tecla') !== 'entrar') return;
        e.preventDefault();
        enviar(pinRef.current);
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [tecladoFisico, digito, borrar, enviar, fijo]);

  // Con bloqueo vigente: el mensaje del servidor si trajo uno, si no la cuenta regresiva.
  const textoMensaje = bloqueado
    ? (mensaje || mensajeRespuesta({ resultado: 'bloqueado', mensaje: '' }, { restanteSeg: restante }))
    : mensaje;
  const totalPuntos = fijo ? largo : Math.max(minimo, pin.length);

  return (
    <div ref={raizRef} role="group" aria-labelledby={idTitulo} data-testid={testId}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, width: '100%', maxWidth: m.ancho, margin: '0 auto' }}>
      <div style={{ textAlign: 'center' }}>
        {soloIconos ? (
          <div aria-hidden="true" data-icono="pin" style={{ fontSize: m.titulo * 2, lineHeight: 1 }}>🔢</div>
        ) : null}
        <h2 id={idTitulo} style={soloIconos ? soloLector : { margin: 0, fontFamily: 'var(--font-display, "Plus Jakarta Sans", system-ui, sans-serif)', fontSize: m.titulo, fontWeight: 700, color: C.fg }}>
          {titulo}
        </h2>
        {subtitulo ? <p style={soloIconos ? soloLector : { margin: '4px 0 0', fontSize: Math.round(m.titulo * 0.7), color: C.mutedFg }}>{subtitulo}</p> : null}
        {!fijo ? <p style={{ margin: '4px 0 0', fontSize: 14, color: C.mutedFg }}>{`El PIN tiene ${textoDigitos(minimo, maximo)}.`}</p> : null}
      </div>

      {soloIconos && bloqueado ? null : (
        <PuntosPin llenos={pin.length} total={totalPuntos} error={errorVisual} tamano={tamano} refPuntos={puntosRef} />
      )}

      {aviso ? (
        <p role="status" style={{ margin: 0, padding: '8px 12px', borderRadius: 12, border: `2px solid ${C.warning}`, color: C.fg, fontSize: 16, textAlign: 'center' }}>
          {aviso}
        </p>
      ) : null}
      {/* Región viva siempre montada (de cables): solo cambia el texto, así el lector de pantalla anuncia
          "Verificando…" de forma fiable. */}
      <p aria-live="polite" data-region="verificando" style={soloIconos ? soloLector : { margin: 0, minHeight: 20, fontSize: 15, color: C.mutedFg, textAlign: 'center' }}>
        {enviando ? 'Verificando…' : ''}
      </p>
      <p role="alert" data-testid="pin-mensaje" style={soloIconos ? soloLector : { margin: 0, minHeight: 28, fontSize: Math.max(16, Math.round(m.titulo * 0.6)), fontWeight: 600, color: C.error, textAlign: 'center' }}>
        {textoMensaje}
      </p>

      {soloIconos && bloqueado ? (
        // Bloqueo sin texto (mantenimiento): reloj de arena grande con los segundos que faltan.
        <div data-testid="pin-bloqueado" role="status" aria-label={`Espere ${restante} segundos`}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <span aria-hidden="true" style={{ fontSize: m.letra * 3.5, lineHeight: 1 }}>⏳</span>
          <span data-testid="espera" style={{ fontSize: m.letra * 2.4, lineHeight: 1, fontWeight: 800, color: C.fg, fontVariantNumeric: 'tabular-nums' }}>
            {restante}
          </span>
        </div>
      ) : (
        <TecladoNumerico
          onDigito={digito}
          onBorrar={borrar}
          onEnviar={fijo ? undefined : () => enviar(pinRef.current)}
          puedeEnviar={pin.length >= minimo}
          deshabilitado={ocupado}
          tamano={tamano}
          extra={extra}
          textoEnviar={enviando ? 'Revisando…' : 'Entrar'}
        />
      )}

      {children}
      {onVolver ? (
        <button type="button" onClick={onVolver} style={{
          ...estiloTactil, minHeight: 56, padding: '0 24px', borderRadius: 16, border: `2px solid ${C.border}`,
          background: C.card, color: C.fg, fontSize: 18, fontWeight: 600, cursor: 'pointer',
        }}>
          {textoVolver}
        </button>
      ) : null}
    </div>
  );
}
